import type { GeneralReport, ReportActivityItem, ReportViewsPoint, SentencePart } from '@discovr/types';
import { DISPLAY_DECIMALS, formatCpv } from '../lib/format';
import { reportingKpiTooltips } from './kpiTooltips';
import { relativeTime } from '../campaign-detail/components/insights/utils/formatters';

export const EMPTY_VALUE = '—';
export const LOW_CONFIDENCE_TEXT = 'Low confidence (under 10 comments)';
export const NO_COMMENTS_YET = 'No comments yet';
/** Report rates are fractions (0–1); multiply by this only for display. */
export const PERCENT = 100;
const CRORE = 1_00_00_000;
const LAKH = 1_00_000;
const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;
const RUPEE = '₹';
const INR_CODE = 'INR';
const NUMBER_LOCALE = 'en-US';
const SENTIMENT_SCORE_DECIMALS = 2;
// Pinned rather than left to the runtime: ICU versions disagree on how many decimals a compact currency keeps.
const COMPACT_CURRENCY_MIN_DECIMALS = DISPLAY_DECIMALS;
const COMPACT_CURRENCY_MAX_DECIMALS = DISPLAY_DECIMALS;

/** A sentiment score as every reporting screen shows it: up to two decimals,
 *  trailing zeros dropped (62, 61.6, 61.57). A missing or non-finite score
 *  shows the empty-value dash rather than "NaN". */
export function formatSentimentScore(score: number | null | undefined): string {
  if (typeof score !== 'number' || !Number.isFinite(score)) return EMPTY_VALUE;
  return String(Number(score.toFixed(SENTIMENT_SCORE_DECIMALS)));
}

// The five KPI tiles, in display order. The one label list both the report
// and the unavailable state render, so the tiles can't drift apart.
const KPI_LABELS = ['Campaign Budget', 'Live Content', 'Views', 'Engagement', 'CPV'] as const;
type KpiLabel = (typeof KPI_LABELS)[number];

export interface KpiStat {
  label: string;
  value: string;
  tooltip?: string;
}

export interface ActivityEntry {
  id: string;
  name: string;
  sentence: string;
  parts: SentencePart[];
  status: string;
  event: string;
  tag: string;
  date: Date;
  avatarUrl?: string;
  detail?: string;
}

interface ScaleUnit {
  size: number;
  suffix: string;
  digits: number;
}

const COUNT_UNITS: readonly ScaleUnit[] = [
  { size: BILLION, suffix: 'B', digits: 2 },
  { size: MILLION, suffix: 'M', digits: 2 },
  { size: THOUSAND, suffix: 'K', digits: 2 },
];
const RUPEE_UNITS: readonly ScaleUnit[] = [
  { size: CRORE, suffix: 'Cr', digits: DISPLAY_DECIMALS },
  { size: LAKH, suffix: 'L', digits: DISPLAY_DECIMALS },
  { size: THOUSAND, suffix: 'K', digits: DISPLAY_DECIMALS },
];

/** Scales to the largest unit the value reaches, moving up one unit when rounding carries it over
 *  (999,999 reads 1.00M, never 1000.00K). Values below the smallest unit stay whole. */
function scaleToUnit(value: number, units: readonly ScaleUnit[], whole: (n: number) => string): string {
  const index = units.findIndex((u) => value >= u.size);
  if (index === -1) return whole(value);
  const unit = units[index] as ScaleUnit;
  const larger = units[index - 1];
  const rounded = Number((value / unit.size).toFixed(unit.digits));
  const chosen = larger && rounded * unit.size >= larger.size ? larger : unit;
  return `${(value / chosen.size).toFixed(chosen.digits)}${chosen.suffix}`;
}

const wholeNumber = (n: number): string => String(Math.round(n));
// Money below the smallest abbreviated unit still carries its decimals.
const exactMoney = (n: number): string => n.toFixed(DISPLAY_DECIMALS);

/** Views tile: always B / M / K, never lakhs. */
function formatCount(n: number): string {
  return scaleToUnit(n, COUNT_UNITS, wholeNumber);
}

function isIndianRupee(currency: string): boolean {
  return currency.toUpperCase() === INR_CODE;
}

function compactCurrency(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(NUMBER_LOCALE, {
      style: 'currency',
      currency,
      notation: 'compact',
      minimumFractionDigits: COMPACT_CURRENCY_MIN_DECIMALS,
      maximumFractionDigits: COMPACT_CURRENCY_MAX_DECIMALS,
    }).format(value);
  } catch (err) {
    // An unrecognised currency code: show the code itself rather than a wrong symbol.
    if (err instanceof RangeError) return `${currency} ${formatCount(value)}`;
    throw err;
  }
}

function exactCurrency(value: number, currency: string): string | undefined {
  try {
    return new Intl.NumberFormat(NUMBER_LOCALE, { style: 'currency', currency }).format(value);
  } catch (err) {
    if (err instanceof RangeError) return undefined;
    throw err;
  }
}

/** Budget tile: Cr / L / K for rupees, as the tiles have always shown it; the currency's own compact form otherwise. */
function formatBudget(value: number, currency: string): string {
  if (!isIndianRupee(currency)) return compactCurrency(value, currency);
  return `${RUPEE}${scaleToUnit(value, RUPEE_UNITS, exactMoney)}`;
}

function budgetTooltip(value: number | null, currency: string, rupeeTooltip: string | undefined): string | undefined {
  if (value === null || value <= 0) return undefined;
  return isIndianRupee(currency) ? rupeeTooltip : exactCurrency(value, currency);
}

function hasValue(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** The five KPI tiles. Formatting only: every number comes from the backend; only a missing value shows "—". */
export function kpiStatsFromReport(kpis: GeneralReport['kpis'], currency: string): KpiStat[] {
  const engagementPercent = hasValue(kpis.engagement_rate) ? kpis.engagement_rate * PERCENT : null;
  const tooltips = reportingKpiTooltips({ budget: kpis.budget ?? 0, reach: kpis.views, engagement: engagementPercent ?? 0 });
  const tiles: Record<KpiLabel, Omit<KpiStat, 'label'>> = {
    'Campaign Budget': {
      value: hasValue(kpis.budget) ? formatBudget(kpis.budget, currency) : EMPTY_VALUE,
      tooltip: budgetTooltip(kpis.budget, currency, tooltips['Campaign Budget']),
    },
    'Live Content': { value: hasValue(kpis.live_content) ? String(kpis.live_content) : EMPTY_VALUE },
    Views: { value: hasValue(kpis.views) ? formatCount(kpis.views) : EMPTY_VALUE, tooltip: tooltips.Views },
    Engagement: { value: engagementPercent === null ? EMPTY_VALUE : `${engagementPercent.toFixed(DISPLAY_DECIMALS)}%`, tooltip: tooltips.Engagement },
    CPV: { value: formatCpv(kpis.cpv, currency) },
  };
  return KPI_LABELS.map((label) => ({ label, ...tiles[label] }));
}

/** The five tiles with no values, for when the report couldn't be loaded:
 *  "—" everywhere rather than a collapsed row or zeros (L8). */
export function unavailableKpiStats(): KpiStat[] {
  return KPI_LABELS.map((label) => ({ label, value: EMPTY_VALUE }));
}

export function activityEntriesFromReport(items: ReportActivityItem[] | null): ActivityEntry[] {
  return (items ?? []).map((item) => ({
    id: item.id,
    name: item.actor,
    sentence: item.sentence,
    parts: item.parts,
    status: item.status,
    event: relativeTime(item.at),
    tag: item.tag,
    date: new Date(item.at),
    avatarUrl: item.creator_avatar || undefined,
    detail: item.detail || undefined,
  }));
}

export function chartPointsFromReport(points: ReportViewsPoint[] | null): Array<{ x: number; reach: number }> {
  return (points ?? []).map((p) => ({ x: Date.parse(p.t), reach: p.total_views }));
}
